<template>
  <UiComponentWrapper
    class="highlighted-posts"
    :class="props.theme"
  >
    <UiComponentContainer class="inner">
      <UiComponentHeading
        as="paragraph"
        :level="3"
        :theme="props.theme"
      >
        <slot />
      </UiComponentHeading>
      <UiComponentWrapper class="list">
        <NuxtLink
          v-for="({ id, image, category, title, readingTime, slug }) in posts"
          :key="`item-${id}`"
          class="item"
          :class="{ larger: posts.length === 6 }"
          :to="
            localePath({
              name: 'blog-slug',
              params: {
                slug,
              },
            })
          "
        >
          <UiComponentWrapper class="head">
            <UiComponentImage
              :key="`item-${id}-image-${imageWidth}x${imageHeight}`"
              :alt="image.alt"
              class="cover"
              :height="image.height"
              :expected-size="imageWidth"
              :extra-params="{
                fit: 'crop',
                h: imageHeight,
                w: imageWidth,
              }"
              :source="image.url"
              use-natural-height
              use-natural-width
              :width="image.width"
            />
          </UiComponentWrapper>
          <UiComponentWrapper class="body">
            <UiComponentWrapper class="tag">
              {{ category }}
            </UiComponentWrapper>
            <UiComponentHeading
              as="paragraph"
              :level="5"
              :theme="props.theme"
            >
              {{ title }}
            </UiComponentHeading>
            <UiComponentParagraph :theme="props.theme">
              {{ readingTime }}
            </UiComponentParagraph>
          </UiComponentWrapper>
        </NuxtLink>
      </UiComponentWrapper>
    </UiComponentContainer>
  </UiComponentWrapper>
</template>

<script setup>
// Composables
const localePath = useLocalePath()
const { width: screenWidth } = useScreenSize()

// Props
const props = defineProps({
  items: {
    required: true,
    type: Array,
  },
  minutesRead: {
    default() {
      return 'min. read'
    },
    required: true,
    type: String,
  },
  theme: {
    default() {
      return 'light'
    },
    required: false,
    type: String,
    validator(value) {
      return ['dark', 'light'].indexOf(value) !== -1
    },
  },
})

// Computed Properties
const imageHeight = computed(() => {
  return screenWidth.value < 1328 ? 180 : 212
})

const imageWidth = computed(() => {
  switch (true) {
    case screenWidth.value < 768:
      return screenWidth.value - 32
    case screenWidth.value >= 768 && screenWidth.value < 1328:
      return (((screenWidth.value - 48) - 40) / 2)
    default:
      return posts.value.length === 6
        ? ((1280 - 80) / 3)
        : ((1280 - 120) / 4)
  }
})

const posts = computed(() => {
  return props.items
    .map(
      ({
        id,
        blogPostCategory,
        content,
        coverImage,
        coverWithImgix,
        slug,
        title,
      }) => {
        return {
          id,
          image: coverWithImgix || coverImage,
          category: blogPostCategory[0].name,
          title,
          readingTime: getReadingTime(content),
          slug,
        }
      },
    )
})

// Methods
function getReadingTime(content) {
  const allContentLength = content.reduce(
    (accumulator, item) => {
      if (item?.__typename !== 'TextRecord') {
        return accumulator
      }
      return accumulator + item.text.length
    },
    0,
  )
  const minutes = Math.max(Math.floor(allContentLength / 1500), 1)
  return `${minutes} ${props.minutesRead}`
}
</script>

<style scoped>
.highlighted-posts {
  @apply justify-center mt-14 py-12;

  @screen md {
    @apply mt-26 py-[96px];
  }

  &.light {
    @apply bg-grey-blue-100;

    .inner {
      .list {
        .item {
          .body {
            .h5 {
              & + p {
                @apply text-grey-blue-600;
              }
            }
          }
        }
      }
    }
  }

  .inner {
    @apply flex-col gap-10;

    .list {
      @apply flex-row flex-wrap gap-10;

      @screen md {
        @apply flex-row flex-wrap;
      }

      .item {
        @apply flex flex-col gap-4 w-full;

        @screen md {
          width: calc((100% - 40px) / 2);
        }

        @screen xl {
          width: calc((100% - 120px) / 4);
        }

        &:hover {
          .head {
            .cover {
              @apply scale-105;
            }
          }
        }

        &.larger {
          @screen xl {
            width: calc((100% - 80px) / 3);
          }
        }

        .head {
          @apply items-center justify-center h-[180px] bg-grey-blue-200 overflow-hidden rounded-3xl;

          @screen xl {
            @apply h-[212px];
          }

          .cover {
            @apply rounded-none transform scale-100 transition-transform duration-600 ease-in-out;
          }
        }

        .body {
          @apply flex-col items-center;

          .tag {
            @apply self-center items-center w-auto h-6 px-2 bg-blizzard-100 font-medium text-blizzard-700 text-xs leading-4 rounded-[8px];
          }

          .h5 {
            @apply mt-3 text-center;

            & + p {
              @apply mt-1 text-center text-sm leading-6;
            }
          }
        }
      }
    }
  }
}
</style>
